import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { If } from 'src/components/If';
import { FullPageSpinner } from 'src/components/Spinner';
import { useDecoratedPostHog } from 'src/providers/posthog';
import { ReactQueryParamsProvider } from 'src/providers/ReactQueryParamsProvider';
import { useAuth } from 'src/services/Auth';
import { useGlobalLoadingStore } from 'src/stores';
import { useUserGuiding } from 'src/lib/services/user-guiding';
import { WebViewAuthService } from 'src/services/Auth/WebView.auth';
import { useBrandsQuery } from 'src/api/queries';
import { useAuthStore } from 'src/services/Auth/Auth.store';
import { signalPageReady } from 'src/utils/page-ready-signal';
import { isAuthFailureError } from 'src/api/Http/http-error-kind.logic';
import { STORAGE_KEYS } from 'src/services/StorageService/StorageService';
import { createBoundedSessionCounter } from 'src/services/Auth/bounded-counter.logic';
import { Logger } from 'src/utils/logger';
import {
  shouldShowAuthLoading,
  shouldShowWebviewAuthError,
  getWebviewAuthErrorKind,
  type WebviewAuthErrorKind,
  shouldSignalWebviewReady,
  shouldAttemptWebviewAuthRecovery,
  isWebviewAuthSettled,
  shouldResetRecoveryBudget,
  getRetryButtonState,
  MAX_WEBVIEW_AUTH_RECOVERIES,
  getRecoveryDelayMs,
  RETRY_FEEDBACK_MS,
  type AuthGateState,
} from './auth-loading-gate.logic';

let tokenRefreshed = false;

// Attempt counter for WebView auth recovery. Durable (sessionStorage) because a successful
// recovery reloads the page (WebView.auth does window.location.replace) and the count must
// survive that reload; exempted from logout cleanup via SESSION_STORAGE_PRESERVED_KEYS,
// since a wiped counter un-bounds the loop it caps.
//
// Uses the SAME primitive as the AUTH_TOKEN navigation budget, and that matters rather than
// being tidiness. The hand-rolled try/catch trio this replaces was storage-ONLY, so in an
// Android WebView with DOM storage disabled every read returned 0: the effect computed
// `read() + 1` = 1 forever, the counter pinned at 1, `recoveryExhausted` (>= 2) never became
// true, so the terminal screen could never render and the WebView sat on an indefinite
// spinner with READY never posted — the exact blank screen this file exists to remove, on
// precisely the devices least able to recover from it. createBoundedSessionCounter keeps an
// in-memory floor for that case, so the bound still works when storage does not.
const recoveryCounter = createBoundedSessionCounter(
  STORAGE_KEYS.SESSION.WV_AUTH_RECOVERY_ATTEMPTS,
  MAX_WEBVIEW_AUTH_RECOVERIES,
);

/**
 * Shown inside the WebView when bounded auth-recovery is exhausted and we still can't
 * load the app. Rendering this instead of the routed page keeps a paying user whose
 * token went stale from being redirected by ProtectedRoute. An actionable state, not
 * a permanent blank.
 */
const WebviewSessionExpired = ({
  kind,
  onRetry,
  isRetrying,
}: {
  kind: WebviewAuthErrorKind;
  onRetry: () => void;
  isRetrying: boolean;
}) => {
  // Retry asks native for a token and refetches brands, which produces nothing observable
  // if native isn't answering — the usual reason recovery exhausted. So show pending
  // immediately, then admit failure at RETRY_FEEDBACK_MS. A successful retry reloads the
  // page or loads brands, so the timer never lands. `isRetrying` is owned by AuthLayout:
  // this component unmounts as soon as it stops being the rendered screen, which would
  // discard local pending state.
  //
  // Deliberately NOT translated, and this is the one screen where that is the correct call
  // rather than a shortcut. i18n runs with `react: { useSuspense: true }` and loads namespaces
  // over HTTP from API_URL (services/i18n/i18n.ts), and the whole app sits inside a single
  // `<Suspense fallback={<FullPageSpinner/>}>` (index.tsx:39). So calling `useTranslation()`
  // here made this component SUSPEND in exactly the two situations it exists for — an offline
  // cold start and a backend outage — React swapped in the full-page spinner, and the
  // signalPageReady effect below never mounted. The escape hatch was gated behind the same
  // backend whose failure it reports, leaving the native overlay up over a spinner: the blank
  // screen this file exists to remove, one layer down.
  //
  // The strings were already English-keyed (`t('No connection')`), and a missing key resolves
  // to the key itself, so nothing about the rendered copy changes.
  const [hasFailed, setHasFailed] = useState(false);

  // Lift the NATIVE loading overlay. A different signal from the READY message AuthLayout
  // posts: WebviewWrapper hides its loader on PAGE_READY only (with a 20s fallback), while
  // READY is consumed by useWebviewAuth. PAGE_READY normally comes from routed pages via
  // usePageReadySignal, and this screen is not a routed page — without this it would sit
  // invisible under the native loader for the full fallback, which is the same blank-screen
  // symptom this file exists to remove.
  useEffect(() => {
    signalPageReady('webview-session-expired');
  }, []);

  // Derive ONCE and let both the button and the failure timer depend on the same boolean.
  // Never wired straight to `isRetrying`: that flag stays true for the whole life of this
  // screen on the failure path, so reading it alone left the only affordance here permanently
  // disabled underneath copy telling the user to try again. And `pending` is exactly "a
  // failure timer is running", so deriving both from it keeps the dep list below correct by
  // construction — listing `[isRetrying, kind]` by hand omitted `hasFailed`, which meant the
  // SECOND tap cleared hasFailed without re-arming anything and re-latched the button forever.
  const retryButton = getRetryButtonState(isRetrying, hasFailed, kind);

  useEffect(() => {
    // No failure timer while offline: telling someone with no signal to close and reopen the
    // app fixes nothing, and the offline copy already names the action that does. Also what
    // keeps the button tappable there instead of latching with nothing left to clear it —
    // see getRetryButtonState, which folds `kind` in for exactly this.
    if (!retryButton.pending) return;
    const timer = setTimeout(() => setHasFailed(true), RETRY_FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [retryButton.pending]);

  // A stale failure verdict must not outlive the diagnosis it was made under: `hasFailed`
  // selects the "close and reopen the app" copy, which is false under "No connection" and
  // useless under a 5xx. If the kind changes, re-wait rather than keep asserting failure.
  useEffect(() => {
    setHasFailed(false);
  }, [kind]);

  const handleClick = () => {
    setHasFailed(false);
    onRetry();
  };

  // "Session expired" is only honest for an actual auth failure. Saying it while the
  // backend is down, or while the device has no signal, misdirects the user and anyone
  // reading their bug report.
  const title =
    kind === 'offline'
      ? 'No connection'
      : kind === 'server'
        ? 'Something went wrong'
        : 'Session expired';

  const body = hasFailed
    ? "We still can't reach the app. Please close it and open it again."
    : kind === 'offline'
      ? 'You appear to be offline. Check your connection and tap retry.'
      : kind === 'server'
        ? 'We could not load your account. This is on our end — tap retry in a moment.'
        : "We couldn't refresh your session. Tap retry to reconnect.";

  return (
    <div className="tw-flex tw-h-screen tw-w-screen tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-p-6 tw-text-center">
      <p className="tw-text-lg tw-font-semibold">{title}</p>
      <p className="tw-text-sm tw-text-gray-500">{body}</p>
      <button
        type="button"
        onClick={handleClick}
        disabled={retryButton.disabled}
        className="tw-rounded-md tw-bg-black tw-px-6 tw-py-2 tw-text-sm tw-font-medium tw-text-white disabled:tw-opacity-50"
      >
        {retryButton.pending ? 'Reconnecting…' : 'Retry'}
      </button>
    </div>
  );
};

const AuthLayout = () => {
  const { mount, dismount, refresh, isInitialLoading } = useAuth();
  const { isLoggedIn, isWebView, isWebViewReady, sessionEpoch, adoptedTokenEpoch } = useAuthStore();
  const { isGlobalLoading, setGlobalLoading } = useGlobalLoadingStore();
  // networkMode 'always' on THIS query specifically, because the gate's terminal state depends
  // on it settling and the default cannot be trusted to let it.
  //
  // React Query v4 defaults to networkMode 'online', and `onlineManager.isOnline()` falls back
  // to `navigator.onLine` (query-core onlineManager.js). Inside an Android WebView that signal
  // is only accurate with ACCESS_NETWORK_STATE, and mobile-app's AndroidManifest.xml declares
  // INTERNET only — so the WebView can report `false` at cold start with a perfectly good
  // connection, and because no transition occurred no `online` event ever fires to correct it.
  // Under the default the query would sit `paused` forever on such a device: no request, no
  // error, no data, and a terminal screen whose Retry refetch is also a no-op. 'always' makes
  // the request actually go out, so the lying signal costs at most one fast failure instead of
  // a dead end — and a genuine offline failure settles into brandsErrored, which the gate and
  // the error copy already handle.
  const brandsQuery = useBrandsQuery({}, { enabled: isLoggedIn, networkMode: 'always' });
  const postHog = useDecoratedPostHog();
  const location = useLocation();
  const userGuiding = useUserGuiding();

  const [currentLocation, setCurrentLocation] = useState(location.pathname);
  const [recoveryAttempts, setRecoveryAttempts] = useState(() => recoveryCounter.read());
  const [isRetrying, setIsRetrying] = useState(false);

  // navigator.onLine, tracked as state so the gate re-evaluates on reconnect.
  //
  // `typeof` rather than `navigator?.onLine`: optional chaining does NOT guard an
  // undeclared identifier — it still throws ReferenceError — so it would be no
  // protection at all in an environment without the global. Unknown is treated as
  // ONLINE, which is the safe direction: it only re-enables recovery, and recovery is
  // itself bounded. Treating unknown as offline would disable recovery entirely and
  // restore the permanent spinner this file exists to remove.
  const [isOffline, setIsOffline] = useState(
    () => typeof navigator !== 'undefined' && navigator.onLine === false,
  );

  useEffect(() => {
    const goOnline = () => setIsOffline(false);
    const goOffline = () => setIsOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  const gateState: AuthGateState = {
    isWebView,
    isWebViewReady,
    isLoggedIn,
    brandsLoaded: !!brandsQuery?.data,
    brandsErrored: !!brandsQuery?.isError,
    // 401/403 means re-auth might help; anything else (500, network) means it cannot, and
    // the terminal screen must say so rather than blaming the user's session.
    brandsErrorIsAuth: isAuthFailureError(brandsQuery?.error),
    // fetchStatus, not isFetching: 'paused' must also
    // count as in-flight. A paused query never issued a request, so the axios timeout
    // can't turn it into an error, and reloading while offline would produce a real
    // network-error page and burn a recovery attempt. It resumes on reconnect.
    // Written positively: `?.fetchStatus !== 'idle'` would evaluate to TRUE if the
    // query object were ever undefined, permanently marking brands as in-flight and
    // hanging the spinner — the exact failure mode this file exists to prevent.
    brandsFetching:
      brandsQuery?.fetchStatus === 'fetching' || brandsQuery?.fetchStatus === 'paused',
    isOffline,
    isRetrying,
    recoveryExhausted: recoveryAttempts >= MAX_WEBVIEW_AUTH_RECOVERIES,
    isGlobalLoading,
    isInitialLoading,
  };

  useEffect(() => {
    // refresh() runs in the WebView too, and is NOT a no-op there: on a stale token it
    // never reaches the network — AuthUtils.isExpired() short-circuits to endSession(),
    // and a server-rejected refresh hits the .catch() which also calls endSession() —
    // so it can log the user out before the recovery logic runs. Survivable, because
    // that degrades into the recovery path below, but don't read the call as harmless.
    //
    // It stays because startSession() is the app's only load-time Pusher connect
    // (Broadcast.user.connect), plus the identify calls and token rotation. Skipping it
    // in the WebView left those users with no realtime channel and a stale token.
    if (!tokenRefreshed) {
      refresh().then(() => setTimeout(() => setGlobalLoading(false), 500));
      tokenRefreshed = true;
    }

    mount();
    return () => {
      dismount();
    };
  }, []);

  useEffect(() => {
    if (currentLocation !== location.pathname) {
      // Commenting out postHog pageview due it constant barraging and hitting posthog limits
      // later will migrate to event specific capture
      // postHog.capture('$pageview');
      setCurrentLocation(location.pathname);
      userGuiding.pageCapture();
    }
  }, [location.pathname]);

  // Derive the gate decisions ONCE per render and depend on the booleans themselves.
  //
  // Listing the individual gateState fields as deps was its own blank-screen bug: the
  // decision also reads isLoggedIn (through shouldShowAuthLoading), which wasn't in
  // the list, and exhaustive-deps is disabled repo-wide so nothing flagged it. A
  // refresh() failure calling endSession() flips isLoggedIn true→false, which stops
  // the spinner and renders the page while no listed dep changed — so READY was never
  // posted and native's overlay stayed up. Depending on the derived boolean makes the
  // dep list correct by construction: any input that changes the decision changes it.
  const showAuthLoading = shouldShowAuthLoading(gateState);
  const showWebviewAuthError = shouldShowWebviewAuthError(gateState);
  const signalWebviewReady = shouldSignalWebviewReady(gateState);

  // Clear the retry flag whenever the terminal screen is no longer the rendered screen.
  //
  // Keyed on the screen no longer showing, not on a success path, because the two obvious
  // reset points are both unreachable in a real failure: if the retry reply carries a token
  // we do not adopt, isLoggedIn stays false so the screen unmounts (cleanup kills its own
  // timeout before it fires) AND brands stays disabled so authSettled never arrives. The
  // flag would latch true, and a latched flag keeps relaxing the !brandsFetching guard —
  // showing "session expired" over an ordinary in-flight request much later.
  useEffect(() => {
    if (!showWebviewAuthError && isRetrying) setIsRetrying(false);
  }, [showWebviewAuthError, isRetrying]);

  // Signal READY to the native host once the WebView shows real content — the app
  // page OR the retry screen OR the login form — i.e. on every terminal path, not
  // only brandsQuery.data. Otherwise native stays on its loading overlay forever.
  useEffect(() => {
    if (signalWebviewReady) {
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({
          type: 'READY',
          secure: true,
        }),
      );
    }
  }, [signalWebviewReady]);

  // A newly ADOPTED native token with no document load to consume it must still re-issue the
  // request the gate is waiting on.
  //
  // Normally `location.replace` does this for free — a new document refetches everything. But
  // planAuthReplyNavigation suppresses that load when the navigation budget is exhausted or the
  // device is offline, and in those cases nothing else refetches: attemptRecovery is false once
  // the terminal screen is up, isWebviewAuthSettled needs brandsLoaded, and
  // Http.requestTokenFromMobile only polls for 5s while a 401 is in flight. A reply landing
  // after that window wrote a perfectly good token to storage and left the user reading "close
  // and reopen the app".
  //
  // Keyed on adoptedTokenEpoch rather than isLoggedIn, because adopting a token when we were
  // already logged in does not move isLoggedIn at all — which is exactly the mid-session case
  // this covers. Guarded on isError so the healthy path adds no request: on a successful load
  // brands has data and there is nothing to re-issue.
  const seenAdoptedTokenEpoch = useRef(adoptedTokenEpoch);
  useEffect(() => {
    if (adoptedTokenEpoch > seenAdoptedTokenEpoch.current) {
      seenAdoptedTokenEpoch.current = adoptedTokenEpoch;
      if (brandsQuery?.isError || !brandsQuery?.data) void brandsQuery?.refetch?.();
    }
  }, [adoptedTokenEpoch]);

  // Hand the recovery budget back when a session is established, so attempts spent before one
  // existed aren't charged to it. Keyed on the session epoch, which only startSession() bumps
  // — never the isLoggedIn edge (adopting a native token IS that edge) and never the logout
  // edge (refresh() → endSession() fires it on every load with a rejected token, once per
  // document). Both would let the reload loop refill its own budget; see shouldResetRecoveryBudget.
  //
  // The AUTH_TOKEN navigation budget is deliberately NOT reset here — see the note on
  // WebViewAuthService.navigations. Its hazard is a superseded reply still in flight, and every
  // moment inside a freshly loaded document is inside that window, including this one.
  const seenSessionEpoch = useRef(sessionEpoch);
  useEffect(() => {
    if (shouldResetRecoveryBudget(sessionEpoch, seenSessionEpoch.current)) {
      // Logged when it actually hands something back, because refilling a loop bound is the
      // one event that could explain a reload loop in a bug report. Logger.error rather than
      // console.error: ERROR is the level that DOES emit in production (utils/logger.ts sets
      // LogLevel.ERROR for PROD), and it adds the origin tag that makes this findable.
      if (recoveryAttempts > 0) {
        Logger.error(
          `Recovery budget handed back to a new session (was ${recoveryAttempts})`,
          'AuthLayout',
        );
      }
      recoveryCounter.clear();
      setRecoveryAttempts(0);
      // Refilling the budget re-arms the RELOAD path, so give the data layer the first
      // attempt: a request that failed under the old token usually just needs retrying with
      // the new one. Without this, a refresh that lands after recovery exhausted takes the
      // terminal screen down and spends the fresh budget on full document loads to fix
      // something a refetch fixes. Guarded on isError so the healthy path adds no request.
      if (brandsQuery?.isError) void brandsQuery?.refetch?.();
    }
    seenSessionEpoch.current = sessionEpoch;
  }, [sessionEpoch]);

  // Deliberately does NOT reset the attempt counter. Resetting it had two problems:
  // it cleared recoveryExhausted, which unmounted the retry screen in the same render
  // as the tap (so the user got a bare spinner instead of any feedback), and it
  // silently re-armed the auto-recovery effect for another MAX_WEBVIEW_AUTH_RECOVERIES
  // full-page reloads. A manual retry is one explicit request: ask native once, show
  // pending, and report failure if nothing happens. A successful reply reloads the
  // page, and the counter is reset on the way out by isWebviewAuthSettled.
  // Refetches brands as well as asking native for a token, because native is not always
  // the party at fault. This screen is reachable with a valid token and a brands request
  // that failed on its own (a 5xx, or a blip that settled to error) — in which case
  // asking native for a token it already gave us changes nothing, and only a refetch can
  // clear the screen. Cheap, and it makes Retry meaningful in both failure modes.
  const handleWebviewAuthRetry = () => {
    setIsRetrying(true);
    // Re-read connectivity on the tap, because `isOffline` is a LATCH and offline is now a
    // terminal screen — so a stale `true` would be a screen the user cannot leave.
    //
    // It can be stale in exactly one direction, and it is not hypothetical: on Android,
    // `navigator.onLine` inside a WebView is only accurate with ACCESS_NETWORK_STATE, and
    // mobile-app's AndroidManifest.xml declares INTERNET only. Without it the WebView can
    // report `false` at cold start with a perfectly good connection — and since no transition
    // occurred, no `online` event ever fires to correct it. Our listener would wait forever.
    //
    // One re-read on an explicit tap costs nothing and is the cheapest way to make the screen
    // impossible to latch: if the reading has since corrected itself (it typically does after
    // any real network activity) the gate drops straight back into normal bounded recovery.
    if (typeof navigator !== 'undefined') setIsOffline(navigator.onLine === false);
    WebViewAuthService.requestAuthFromNative();
    void brandsQuery?.refetch?.();
  };

  // WebView recovery: if the screen is stuck loading, or brands 401'd on a stale
  // token, ask native for a fresh token. WebView.auth reloads the page onto the
  // authed route on receipt, so this is the "try again and load" path that turns a
  // permanent blank into a recovery. Bounded so it can never loop if native never
  // answers; once exhausted the gate renders the retry screen instead.
  //
  // This is the page-level recovery, distinct from Http.requestTokenFromMobile (which
  // retries once, ~5s, on any single 401). The Http-level retry is not sufficient on
  // its own: when it fails, React Query marks brands `isError` and — without this —
  // the page would either hang on the spinner or fall through to the paywall. This
  // effect bounds the re-auth at the page level and reloads onto the authed route.
  // Depend on the derived decisions, not on a hand-listed subset of their inputs — a
  // dep list that silently omits an input is what caused the READY bug above.
  const authSettled = isWebviewAuthSettled(gateState);
  const attemptRecovery = shouldAttemptWebviewAuthRecovery(gateState, recoveryAttempts);
  // Paced to native's reply round-trip, not to how fast we'd like to retry — see
  // getRecoveryDelayMs. The timer is cancelled whenever attemptRecovery flips false,
  // which includes the handshake completing, so waiting longer costs nothing on a
  // load that succeeds.
  const recoveryDelay = getRecoveryDelayMs(recoveryAttempts);

  useEffect(() => {
    if (authSettled) {
      if (recoveryAttempts !== 0) {
        recoveryCounter.clear();
        setRecoveryAttempts(0);
      }
      return;
    }

    if (!attemptRecovery) return;

    const timer = setTimeout(() => {
      // bump() persists AND keeps an in-memory floor, so the count still advances when
      // sessionStorage is unavailable. The previous read-add-write trio silently pinned at 1
      // there, which meant recoveryExhausted never arrived and the spinner never terminated.
      setRecoveryAttempts(recoveryCounter.bump());
      WebViewAuthService.requestAuthFromNative();
    }, recoveryDelay);

    return () => clearTimeout(timer);
  }, [authSettled, attemptRecovery, recoveryDelay, recoveryAttempts]);

  if (showWebviewAuthError) {
    return (
      <WebviewSessionExpired
        kind={getWebviewAuthErrorKind(gateState)}
        onRetry={handleWebviewAuthRetry}
        isRetrying={isRetrying}
      />
    );
  }

  return (
    <React.Fragment>
      <If
        when={showAuthLoading}
        else={
          <ReactQueryParamsProvider>
            <Outlet />
          </ReactQueryParamsProvider>
        }
      >
        <FullPageSpinner />
      </If>
    </React.Fragment>
  );
};

export default AuthLayout;
